import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { getPropertyConfig } from '../utils/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, User, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const AVAILABLE_ROLES = [
  { id: 'Admin', label: 'Admin' },
  { id: 'Purchasing', label: 'Purchasing' },
  { id: 'Finance', label: 'Finance' },
  { id: 'Receiving', label: 'Receiving' },
  { id: 'StoreRequisition', label: 'Store Requisition' }
];

const AVAILABLE_DEPARTMENTS = [
  { id: 'FB', label: 'F&B' },
  { id: 'Housekeeping', label: 'Housekeeping' },
  { id: 'General Store', label: 'General Store' },
  { id: 'Engineering', label: 'Engineering' }
];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) return;
    
    try {
      await api.delete(`/users/${username}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2">Users</h1>
          <p className="text-muted-foreground">Manage system users</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-user-button">
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
            </DialogHeader>
            <UserForm 
              onSuccess={() => {
                setDialogOpen(false);
                fetchUsers();
              }} 
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead>
              <tr className="border-b bg-secondary/50">
                <th className="text-left p-3 font-semibold">Username</th>
                <th className="text-left p-3 font-semibold">Full Name</th>
                <th className="text-left p-3 font-semibold">Roles</th>
                <th className="text-left p-3 font-semibold">Departments</th>
                <th className="text-left p-3 font-semibold">Created</th>
                <th className="text-center p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.username} className="border-b hover:bg-accent/20 transition-colors">
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{user.username}</span>
                    </div>
                  </td>
                  <td className="p-3">{user.full_name || '-'}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {(user.roles || []).map((role) => (
                        <span key={role} className="status-badge bg-primary/10 text-primary border-primary/20 text-xs">
                          {role}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {(user.departments || []).map((dept) => (
                        <span key={dept} className="status-badge bg-blue-100 text-blue-700 border-blue-200 text-xs">
                          {dept}
                        </span>
                      ))}
                      {(!user.departments || user.departments.length === 0) && <span className="text-muted-foreground text-xs">-</span>}
                    </div>
                  </td>
                  <td className="p-3">{new Date(user.created_at).toLocaleDateString()}</td>
                  <td className="p-3">
                    <div className="flex items-center justify-center space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(user)}
                        title="Edit User"
                      >
                        <Edit className="h-4 w-4 text-primary" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(user.username)}
                        title="Delete User"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User - {selectedUser?.username}</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <UserForm 
              editMode={true}
              existingUser={selectedUser}
              onSuccess={() => {
                setEditDialogOpen(false);
                setSelectedUser(null);
                fetchUsers();
              }} 
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UserForm({ onSuccess, editMode = false, existingUser = null }) {
  const propConfig = getPropertyConfig();
  const showDepartments = propConfig?.has_po === false; // Show for Golden Tulip

  const [formData, setFormData] = useState(
    editMode && existingUser ? {
      username: existingUser.username,
      password: '',
      full_name: existingUser.full_name || '',
      roles: existingUser.roles || [],
      departments: existingUser.departments || []
    } : {
      username: '',
      password: '',
      full_name: '',
      roles: ['Purchasing'],
      departments: []
    }
  );

  const handleRoleChange = (roleId, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        roles: [...prev.roles, roleId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        roles: prev.roles.filter(r => r !== roleId)
      }));
    }
  };

  const handleDeptChange = (deptId, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        departments: [...prev.departments, deptId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        departments: prev.departments.filter(d => d !== deptId)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.roles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }

    try {
      if (editMode) {
        const updateData = {
          full_name: formData.full_name,
          roles: formData.roles,
          departments: formData.departments
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await api.put(`/users/${existingUser.username}`, updateData);
        toast.success('User updated successfully');
      } else {
        if (!formData.password) {
          toast.error('Password is required');
          return;
        }
        await api.post('/auth/register', formData);
        toast.success('User created successfully');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'create'} user`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Username</Label>
        <Input
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          required
          disabled={editMode}
          className={editMode ? 'bg-gray-100' : ''}
        />
      </div>

      <div>
        <Label>{editMode ? 'New Password (leave blank to keep current)' : 'Password'}</Label>
        <Input
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          required={!editMode}
          placeholder={editMode ? 'Leave blank to keep current password' : ''}
        />
      </div>

      <div>
        <Label>Full Name</Label>
        <Input
          value={formData.full_name}
          onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
        />
      </div>

      <div>
        <Label className="mb-3 block">Roles (select one or more)</Label>
        <div className="space-y-2 bg-secondary/20 p-3 rounded-lg">
          {AVAILABLE_ROLES.map((role) => (
            <label key={role.id} className="flex items-center space-x-3 cursor-pointer hover:bg-accent/30 p-2 rounded">
              <input
                type="checkbox"
                checked={formData.roles.includes(role.id)}
                onChange={(e) => handleRoleChange(role.id, e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm font-medium">{role.label}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          User can have multiple roles. Select all applicable roles.
        </p>
      </div>

      {showDepartments && (
        <div>
          <Label className="mb-3 block">Departments (akses gudang)</Label>
          <div className="space-y-2 bg-blue-50 p-3 rounded-lg">
            {AVAILABLE_DEPARTMENTS.map((dept) => (
              <label key={dept.id} className="flex items-center space-x-3 cursor-pointer hover:bg-blue-100 p-2 rounded">
                <input
                  type="checkbox"
                  checked={formData.departments.includes(dept.id)}
                  onChange={(e) => handleDeptChange(dept.id, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium">{dept.label}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Pilih department yang bisa diakses user ini. Admin bisa akses semua.
          </p>
        </div>
      )}

      <Button type="submit" className="w-full btn-primary">
        {editMode ? 'Update User' : 'Create User'}
      </Button>
    </form>
  );
}
